import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { DATA_SELECTOR_INJECTION_TOKEN, DialogSize, SharedModule, SnackbarService, SnackbarType, TrackService } from '@aiwyn/shared';
import { InviteContactToItems } from '../../../../state/cxp-crl-detail.actions';
import { CrlInviteTreeItem, CxpCrlDetailSelectors } from '../../../../state/cxp-crl-detail-state.selectors';
import {
  CrlInviteItemTreeComponent,
  CrlInviteTreeRow,
  CrlInviteTreeRowSection,
} from './crl-invite-item-tree/crl-invite-item-tree.component';

/** Injected input for the Invite dialog — the item the dialog was opened from. */
export interface CrlInviteDialogData {
  itemId: number;
}

/** Shown beside an item whose access list already includes the address being typed. */
const ALREADY_HAS_ACCESS_REASON = 'Already has access';
/** Shown beside an item nobody new can be added to (restricted, or finished). */
const CANNOT_SHARE_REASON = 'Unavailable';

/**
 * True once the value is a complete address. Runs the form's own validator so
 * the tree reacts to exactly what the Email field accepts, and a half-typed
 * address disables nothing.
 */
function isCompleteEmail(value: string): boolean {
  return value.length > 0 && Validators.email(new FormControl(value)) === null;
}

function toTreeRow(item: CrlInviteTreeItem, matchEmail: string): CrlInviteTreeRow {
  const alreadyHasAccess = matchEmail !== '' && item.assigneeEmails.includes(matchEmail);
  if (alreadyHasAccess) {
    return { id: item.id, name: item.name, displayNumber: item.displayNumber, isDisabled: true, disabledReason: ALREADY_HAS_ACCESS_REASON };
  }

  return {
    id: item.id,
    name: item.name,
    displayNumber: item.displayNumber,
    isDisabled: !item.canInvite,
    disabledReason: item.canInvite ? null : CANNOT_SHARE_REASON,
  };
}

/**
 * CRL "Invite" dialog (PP-3642 redesign) — the simplified replacement for the old
 * reassign modal. A user enters a single email and picks the items it should
 * reach; the backend matches the address to an existing firm contact and assigns
 * it to each selected item (or provisions a secure link server-side).
 * Invite-only: no remove-access, no owner/assignee gating — the affordance shows
 * for everyone and the backend authorizes.
 *
 * The tree is pre-ticked with the item the dialog was opened from, and the user
 * is free to untick it. As soon as the Email field holds a complete address, any
 * item whose access list already contains it is disabled and dropped from the
 * selection; editing the address away re-enables the item but leaves it unticked,
 * so nothing is ever re-selected behind the user's back.
 *
 * Per cxp-dialog this component dispatches the NGXS `InviteContactToItems` action
 * (the state handler reloads the detail so the new contact surfaces in the rail's
 * Client access). On success it snackbars + closes with `true`; on error it
 * snackbars and stays open.
 */
@Component({
  selector: 'cxp-crl-invite-dialog',
  templateUrl: './crl-invite-dialog.component.html',
  styleUrls: ['./crl-invite-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, ReactiveFormsModule, CrlInviteItemTreeComponent],
  providers: [{ provide: DATA_SELECTOR_INJECTION_TOKEN, useValue: 'cxp-crl-invite-dialog' }],
})
export class CrlInviteDialogComponent {
  protected readonly dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);
  protected readonly DialogSize = DialogSize;

  protected readonly data = inject<CrlInviteDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<CrlInviteDialogComponent>);
  private readonly store = inject(Store);
  private readonly snackbar = inject(SnackbarService);
  private readonly trackService = inject(TrackService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isSubmitting = signal(false);

  protected readonly inviteForm = new FormGroup({
    displayName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    message: new FormControl('', { nonNullable: true }),
  });

  /** The request's whole section/item tree, scoped server-side to what this caller can see. */
  private readonly inviteTree = this.store.selectSignal(CxpCrlDetailSelectors.getInviteTreeSections);

  private readonly requestDisplay = this.store.selectSignal(CxpCrlDetailSelectors.getSelectedRequestDisplay);

  /**
   * Heading above the tree — the request being shared, as the legacy assign
   * dialog showed it. The name is null until the detail loads, so it falls back
   * to naming what the tree contains rather than rendering an empty line.
   */
  protected readonly treeHeading = computed(() => this.requestDisplay().name?.trim() || 'Items');

  private readonly emailValue = toSignal(this.inviteForm.controls.email.valueChanges, { initialValue: '' });

  /** The address being typed, normalized for matching; empty while it is incomplete. */
  private readonly matchEmail = computed(() => {
    const email = this.emailValue().trim().toLowerCase();
    return isCompleteEmail(email) ? email : '';
  });

  /** Item ids the invite will be sent for; the opening item starts ticked. */
  private readonly selectedItemIds = signal<ReadonlySet<number>>(new Set([this.data.itemId]));

  protected readonly treeSections = computed<CrlInviteTreeRowSection[]>(() => {
    const matchEmail = this.matchEmail();
    return this.inviteTree().map((section) => ({
      id: section.id,
      name: section.name,
      identifier: section.identifier,
      rows: section.items.map((item) => toTreeRow(item, matchEmail)),
    }));
  });

  /** Memoized so the tree's input keeps its identity between unrelated renders. */
  protected readonly selectedItemIdList = computed<number[]>(() => [...this.selectedItemIds()]);

  private readonly disabledItemIds = computed<ReadonlySet<number>>(
    () => new Set(this.treeSections().flatMap((section) => section.rows.filter((row) => row.isDisabled).map((row) => row.id))),
  );

  private readonly enabledItemIds = computed<ReadonlySet<number>>(
    () => new Set(this.treeSections().flatMap((section) => section.rows.filter((row) => !row.isDisabled).map((row) => row.id))),
  );

  /**
   * What Send actually invites: the selection intersected with the items the
   * tree is currently offering. Deriving it from the rows as they are right now
   * keeps "only a selectable item is ever invited" a local guarantee, instead of
   * one that depends on the pruning effect having flushed first — and an id the
   * tree does not (or no longer) lists cannot slip through either.
   */
  private readonly sendableItemIds = computed<number[]>(() => {
    const enabled = this.enabledItemIds();
    return this.selectedItemIdList().filter((id) => enabled.has(id));
  });

  protected readonly hasSelection = computed(() => this.sendableItemIds().length > 0);

  constructor() {
    // A row the email match (or a reload) has just disabled leaves the selection.
    // This cannot be a computed over the raw selection: re-enabling a row must
    // leave it unticked, so the drop has to be recorded rather than derived.
    effect(() => {
      const disabledIds = this.disabledItemIds();

      untracked(() =>
        this.selectedItemIds.update((selected) => {
          const kept = [...selected].filter((id) => !disabledIds.has(id));
          return kept.length === selected.size ? selected : new Set(kept);
        }),
      );
    });
  }

  /**
   * The tree, for the Expand All / Collapse All links. They sit in the pane's
   * header rather than in the tree because the tree's own box scrolls, and the
   * links must stay put; the tree exposes the expansion state and the two
   * commands for exactly this.
   */
  protected readonly itemTree = viewChild(CrlInviteItemTreeComponent);

  protected onSelectionChange(itemIds: number[]): void {
    this.selectedItemIds.set(new Set(itemIds));
  }

  protected onExpandAll(): void {
    this.itemTree()?.expandAll();
  }

  protected onCollapseAll(): void {
    this.itemTree()?.collapseAll();
  }

  /** Cancel → close without inviting. */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Validate + send the invite for every selected item. On success snackbars and
   * closes with `true`, on error snackbars and stays open so the user can retry.
   */
  onSend(): void {
    this.inviteForm.markAllAsTouched();
    const itemIds = this.sendableItemIds();
    if (this.inviteForm.invalid || this.isSubmitting() || itemIds.length === 0) {
      return;
    }

    const { email, displayName, message } = this.inviteForm.getRawValue();
    this.isSubmitting.set(true);
    // `openedFromItemId` is where the dialog was opened, which the user may well
    // have unticked — `itemCount` is what the invite actually covers.
    this.trackService.track('CXP CRL Item Invite Sent', { openedFromItemId: this.data.itemId, itemCount: itemIds.length });

    this.store
      .dispatch(new InviteContactToItems(itemIds, email.trim(), displayName.trim(), message.trim()))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackbar.open('Invitation sent.', SnackbarType.SUCCESS);
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackbar.open('That invitation could not be sent. Please try again.', SnackbarType.ERROR);
          this.isSubmitting.set(false);
        },
      });
  }
}
